import type { JSX } from 'react';
import { useState } from 'react';

import { CheckCircle2, Code2, Loader2, Send, XCircle } from 'lucide-react';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import type { Webhook, WebhookTestResult } from '../types/webhook';

interface WebhookTestDialogProps {
  open: boolean;
  onClose: () => void;
  webhook: Webhook | null;
  onTest: (webhookId: number) => Promise<WebhookTestResult>;
}

const WebhookTestDialog = ({ open, onClose, webhook, onTest }: WebhookTestDialogProps): JSX.Element | null => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<WebhookTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTest = async (): Promise<void> => {
    if (!webhook) {
      return;
    }
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const testResult = await onTest(webhook.id);
      setResult(testResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al probar el webhook');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    onClose();
  };

  if (webhook === null) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] gap-4 overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Probar webhook</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 pt-1">
          <div className="rounded-lg border border-border-subtle bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            Se enviará un mensaje de prueba al webhook para verificar que la configuración es correcta.
          </div>

          <div className="rounded-lg border border-border-subtle bg-bg-surface-alt p-3">
            <p className="m-0 mb-1 text-xs font-medium text-muted-foreground">URL del webhook</p>
            <p className="m-0 break-all font-mono text-xs text-foreground">{webhook.url}</p>
          </div>

          {!result && !error && !isLoading ? (
            <p className="m-0 py-2 text-center text-sm text-muted-foreground">
              Haz clic en &quot;Enviar prueba&quot; para verificar la conexión
            </p>
          ) : null}

          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-5 shrink-0 animate-spin" aria-hidden />
              Enviando mensaje de prueba…
            </div>
          ) : null}

          {error ? (
            <div
              className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2
                text-sm text-destructive"
              role="alert"
            >
              <XCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          ) : null}

          {result ? (
            <div
              className={cn(
                'rounded-lg border p-3',
                result.success ? 'border-success/50 bg-success/10' : 'border-destructive/50 bg-destructive/10'
              )}
            >
              <div className="mb-2 flex items-center gap-2">
                {result.success ? (
                  <CheckCircle2 className="size-5 shrink-0 text-success" aria-hidden />
                ) : (
                  <XCircle className="size-5 shrink-0 text-destructive" aria-hidden />
                )}
                <span className={cn('font-semibold', result.success ? 'text-success' : 'text-destructive')}>
                  {result.success ? '¡Prueba exitosa!' : 'Prueba fallida'}
                </span>
              </div>
              <dl className="m-0 space-y-1 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <dt className="text-muted-foreground">Código de estado:</dt>
                  <dd className="m-0 font-mono">{result.statusCode ?? 'N/A'}</dd>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <dt className="text-muted-foreground">Tiempo de respuesta:</dt>
                  <dd className="m-0">{result.responseTime} ms</dd>
                </div>
                {result.error ? (
                  <div className="mt-2">
                    <dt className="text-muted-foreground">Error:</dt>
                    <dd className="m-0 mt-0.5 font-mono text-destructive">{result.error}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}

          {result?.testPayload ? (
            <Accordion type="single" collapsible className="w-full border-0">
              <AccordionItem value="payload" className="rounded-lg border border-border-subtle">
                <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">
                  <span className="flex items-center gap-2">
                    <Code2 className="size-4 text-muted-foreground" aria-hidden />
                    Ver payload enviado
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <pre
                    className="max-h-[300px] overflow-auto rounded-md bg-neutral-900 p-3 text-xs text-neutral-100
                      whitespace-pre-wrap break-words"
                  >
                    {JSON.stringify(result.testPayload, null, 2)}
                  </pre>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : null}
        </div>

        <DialogFooter className="border-t-0 bg-transparent p-0 sm:justify-end">
          <Button type="button" variant="outline" disabled={isLoading} onClick={handleClose}>
            Cerrar
          </Button>
          <Button
            type="button"
            disabled={isLoading}
            className="gap-2"
            onClick={() => {
              handleTest().catch(() => {});
            }}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            {result ? 'Probar de nuevo' : 'Enviar prueba'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookTestDialog;
